import { useEffect, useState, useContext, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import { Container, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../../routing/LangRouter";

//import redux store
import { saveFlightData, saveTicketSelectedFilters, saveTicketSort } from "../../../store/actions";

//import custom hooks
import useHeaderInfo from "../../../hooks/useHeaderInfo";
import useFlightList from "../../../hooks/useFlightList";

//import constants
import { ItineraryContext } from "../../../constants/layouts/itinerary-layout";
import { BREAKPOINT, IS_MOBILE, WIDTH } from "../../../constants/breakpoints";

//import utils
import { scrollToPosition } from "../../../utils/utility";

//import general components
import MobileHeader from "../../../components/common/mobile-header/mobile-header";
import Loading from "../../../components/common/loading/loading";
import BaseSort from "../../../components/common/base-sort/base-sort";
import WithoutDestinationModal from "../../../components/common/without-modal/without-destination-modal";
import FilterSortActions from "../../../components/common/filter-sort-actions/filter-sort-actions";
import ExpirationResultModal from "../../../components/common/expiration-result-modal/expiration-result-modal";
import EmptyOrErrorState from "../../../components/common/empty-or-error-state/empty-or-error-state";
import { BaseButton } from "../../../components/common/base-button/base-button";

//import view components
import FlightSearchForm from "../../../components/view/flight/domestic/flight-search-form";
import FlightList from "../../../components/view/flight/domestic/flight-list";
import RecommendedFlight from "../../../components/view/flight/domestic/recommended-flight";
import FlightDetails from "../../../components/view/flight/domestic/flight-details";
import WithoutDestinationDateModal from "../../../components/common/without-modal/without-destination-date-modal";
import WithoutDateModal from "../../../components/common/without-modal/without-date-modal";
import FlightSkeleton from "../../../components/view/flight/domestic/flight-skeleton";
import FlightFilter from "../../../components/view/flight/domestic/flight-filter";

//import images and icons
import { Edit2, Warning2, Notification, Airplane, InfoCircle } from "iconsax-react";

function DomesticFlight({ dispatch, flightData, ticketSort, ticketSelectedFilters }) {
    //general variables
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [requestFlag, setRequestFlag] = useState(false);
    const accessToken = localStorage.getItem("accessToken");
    const [uid, setUid] = useState(null);
    const [requestExpirationDate, setRequestExpirationDate] = useState("");
    const [isError, setIsError] = useState(false);

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const { isItinerary, isAutomaticReserve, isAccommodation } = generalInformation;
    const isTwoWay = domesticFlightInformation?.returnDate;

    // Memoize departure and return flight lists for performance optimization
    const { flightListData } = useMemo(() => {
        setUid(flightData?.data?.uid);
        return {
            flightListData: flightData?.data?.list,
        };
    }, [flightData]);

    //list of sorting options
    const optionListSort = [
        { value: "4", label: `${t("cheapest")}`, checked: false },
        { value: "3", label: `${t("expensive")}`, checked: false },
        { value: "1", label: `${t("earliest")}`, checked: true },
        { value: "2", label: `${t("lastest")}`, checked: false },
    ];

    //remove session items
    useEffect(() => {
        if (!isItinerary) {
            sessionStorage.removeItem("selectedFlight");
            if (!!domesticFlightInformation) {
                sessionStorage.setItem("domesticFlightInformation", JSON.stringify(domesticFlightInformation));
            }
        }

        if (isAutomaticReserve) {
            delete generalInformation.isAutomaticReserve;
            sessionStorage.setItem("generalInformation", JSON.stringify(generalInformation));
        }
    }, []);

    //get flight list
    const { getFlightList } = useFlightList(
        domesticFlightInformation,
        uid,
        ticketSort,
        ticketSelectedFilters,
        setRequestFlag,
        setLoading,
        setIsError,
    );
    useEffect(() => {
        if (!!domesticFlightInformation?.departure && !!domesticFlightInformation?.destination && !!domesticFlightInformation?.departureDate) {
            getFlightList();
        }
    }, []);

    useEffect(() => {
        if (requestFlag) {
            getFlightList();
        }
    }, [requestFlag]);

    //set fligth list
    const [flightList, setFlightList] = useState([]);
    useEffect(() => {
        setRequestExpirationDate(flightData?.data?.exp_at);
        if (!!flightListData) {
            setFlightList(flightListData);
        } else setFlightList([]);
    }, [flightListData]);

    //scroll to top page
    useEffect(() => {
        scrollToPosition(0, 0);
    }, [flightList]);

    // sort initial value
    useEffect(() => {
        dispatch(saveTicketSort("4"));
    }, []);

    // handle sort change
    const handleSelectedRadio = value => {
        dispatch(saveTicketSort(value));
    };

    //handle without destination,without date,and without destination-date modal
    const [openWithoutDate, setOpenWithoutDate] = useState(false);
    const [openWithoutDestination, setOpenWithoutDestination] = useState(false);
    const [openWithoutDestinationDate, setOpenWithoutDestinationDate] = useState(false);
    useEffect(() => {
        const destination = domesticFlightInformation?.destination;
        const departureDate = domesticFlightInformation?.departureDate;
        const returnDate = domesticFlightInformation?.returnDate;
        if (destination !== null && departureDate === null && returnDate === null) setOpenWithoutDate(true);
        else setOpenWithoutDate(false);

        if (destination === null && !!departureDate && !!returnDate) setOpenWithoutDestination(true);
        else setOpenWithoutDestination(false);

        if (destination === null && departureDate === null && returnDate === null) setOpenWithoutDestinationDate(true);
        else setOpenWithoutDestinationDate(false);
    }, []);

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    const resetFlightList = () => {
        dispatch(saveFlightData(null));
    };

    ////mobile functions
    const goToBack = () => {
        if (isItinerary) {
            navigate(routes[routes.findIndex(route => route === location.pathname) - 1]);
        } else {
            navigate(`/${locale}`);
        }
    };

    //title for the mobile header
    const { headerTitle, headerDetails } = useHeaderInfo("flight", domesticFlightInformation);

    //handle show edit search form and flight details
    const handleEdittSearch = action => {
        const params = { action };
        setSearchParams(params, { replace: true });
    };

    const [openSearchForm, setOpenSearchForm] = useState(false);
    const [openFlightDetails, setOpenFlightDetails] = useState(false);
    useEffect(() => {
        if (searchParams.get("action") === "edit") setOpenSearchForm(true);
        else setOpenSearchForm(false);

        if (WIDTH < BREAKPOINT) {
            if (searchParams.get("action") === "flight-details") setOpenFlightDetails(true);
            else setOpenFlightDetails(false);
        }
    }, [searchParams]);

    const handleReserve = () => {
        navigate(`/${locale}/flight-automatic-reserve`);
        sessionStorage.setItem(
            "generalInformation",
            JSON.stringify({
                ...generalInformation,
                isAutomaticReserve: true,
            }),
        );
    };

    //skip
    const navigatePage = page => {
        setRoutes([...routes, `/${locale}/itinerary/${page}`]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, `/${locale}/itinerary/${page}`]));
        navigate(`/${locale}/itinerary/${page}`);
    };
    const skipLevel = () => {
        resetFilters();
        if (isAccommodation) {
            navigatePage("hotel");
        } else {
            navigatePage("itinerary-page");
        }
    };

    return (
        <Grid
            container
            item
            xs={12}
            justifyContent="center"
            rowGap={{ xs: 2, md: 5 }}
            mt={openSearchForm || IS_MOBILE ? 6 : 0}
            mb={{ xs: 10, md: 5 }}
        >
            {loading && <Loading />}

            <MobileHeader
                title={headerTitle}
                details={headerDetails}
                edit={<Edit2 size="20" color="var(--primary-color)" onClick={() => handleEdittSearch("edit")} />}
                justifyContent="flex-start"
                isButton={false}
                handleClick={goToBack}
            />

            <FlightSearchForm setRequestFlag={setRequestFlag} openSearchForm={openSearchForm} setUid={setUid} />

            {!openFlightDetails && IS_MOBILE ? (
                <Grid container item xs={11} mt={2}>
                    <FilterSortActions onFilterClick={() => setIsFilterOpen(true)} onSortClick={() => setIsSortOpen(true)} />
                </Grid>
            ) : null}

            {(isItinerary || !!accessToken) && !openFlightDetails && !isTwoWay ? <RecommendedFlight flightList={flightList} /> : null}

            <Container maxWidth="xl">
                {loading ? (
                    <FlightSkeleton />
                ) : isError ? (
                    <EmptyOrErrorState
                        title={t("errorMessage")}
                        handleClick={getFlightList}
                        actionLabel={t("retry")}
                        icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                    />
                ) : !flightList?.length ? (
                    <EmptyOrErrorState
                        title={t("fligthListMessage")}
                        icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                        actionLabel={isItinerary ? t("nextLevel") : ""}
                        handleClick={isItinerary ? skipLevel : null}
                    />
                ) : (
                    <Grid container item xs={12} justifyContent="center">
                        {openFlightDetails ? (
                            <FlightDetails />
                        ) : (
                            <Grid container item xs={12} rowGap={{ xs: 2, md: 5 }}>
                                <Grid container item xs={12} justifyContent="space-between" alignItems="flex-start">
                                    <Grid
                                        container
                                        item
                                        xs={12}
                                        md={2}
                                        alignItems={{ xs: "flex-end", md: "flex-start" }}
                                        sx={{ position: "sticky !important", top: isItinerary ? "8rem" : "5rem", zIndex: 10 }}
                                    >
                                        <FlightFilter open={isFilterOpen} setOpen={setIsFilterOpen} setRequestFlag={setRequestFlag} setUid={setUid} />
                                    </Grid>

                                    <Grid container item xs={12} md={9.8} rowGap={2} alignItems="flex-start">
                                        <Grid container item xs={12} justifyContent="space-between" alignItems="center">
                                            <BaseSort
                                                open={isSortOpen}
                                                setOpen={setIsSortOpen}
                                                optionList={optionListSort}
                                                handleChange={handleSelectedRadio}
                                                defaultValue={ticketSort}
                                                setRequestFlag={setRequestFlag}
                                            />
                                            {!isTwoWay && (
                                                <Grid container item xs={12} md={5} justifyContent="flex-end">
                                                    <Grid container item justifyContent="space-evenly" rowGap={2}>
                                                        <Grid container item xs={12} md={5}>
                                                            <BaseButton
                                                                rightIcon={<Notification />}
                                                                text={t("General.Notify When Available")}
                                                                color="light-gray"
                                                                size="medium"
                                                                radius="radius-10"
                                                                handleClick={() => navigate(`/${locale}/flight-availability`)}
                                                            />
                                                        </Grid>
                                                        <Grid container item xs={12} md={5}>
                                                            <BaseButton
                                                                rightIcon={<Airplane />}
                                                                text={t("General.Reserve When Available")}
                                                                color="light-gray"
                                                                size="medium"
                                                                radius="radius-10"
                                                                handleClick={handleReserve}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </Grid>

                                        <FlightList
                                            flightList={flightList}
                                            loading={loading}
                                            setRequestFlag={setRequestFlag}
                                            setUid={setUid}
                                            resetFilters={resetFilters}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Container>

            <WithoutDateModal open={openWithoutDate} setOpen={setOpenWithoutDate} setRequestFlag={setRequestFlag} />
            <WithoutDestinationModal open={openWithoutDestination} setOpen={setOpenWithoutDestination} setRequestFlag={setRequestFlag} />
            <WithoutDestinationDateModal open={openWithoutDestinationDate} setOpen={setOpenWithoutDestinationDate} setRequestFlag={setRequestFlag} />
            <ExpirationResultModal
                requestExpirationDate={requestExpirationDate}
                setRequestFlag={setRequestFlag}
                uid={uid}
                setUid={setUid}
                resetList={resetFlightList}
                resetFilters={resetFilters}
            />
        </Grid>
    );
}

export default connect(mapStateToProps)(DomesticFlight);

function mapStateToProps(state) {
    return {
        flightData: state.flightData,
        ticketSort: state.ticketSort,
        ticketSelectedFilters: state.ticketSelectedFilters,
    };
}
